import { Card, Spin, Tooltip } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import '../../styles/_medOrderHistory.scss';
import moment from 'moment';
import {
    EmrSyncStatus,
    MedicationStatusEnum,
    MedicationStatusLabels,
    OrderSource,
} from '../../models/MedOrders';
import {
    MedOrderResponse,
    OrderSourceEnum,
    OrderStatusEmrEnum,
} from '../../models/PatientModels';
import {
    OrderContainer,
    OrderItem,
    OrderItemInline,
    OrderNotFound,
    OrderTitle,
} from '../Orders/OrderCard';
import { OrderTab, OrderTabGrid } from '../Orders/OrderTab';
import { useParams } from 'react-router-dom';

const MedOrderItem = ({ order }: { order: MedOrderResponse }) => {
    const [visible, setVisible] = useState(false);

    const source = useMemo(() => {
        if (typeof order.orderSource === 'number' && order.orderSource >= 0) {
            return {
                color: OrderSource[order.orderSource as OrderSourceEnum].color,
                label: OrderSource[order.orderSource as OrderSourceEnum].label,
            };
        }
        return { label: 'N/A', color: '' };
    }, [order.orderSource]);

    const emrSyncStatus = useMemo(() => {
        if (
            typeof order.orderStatusEmr === 'number' &&
            order.orderStatusEmr >= 0
        ) {
            return EmrSyncStatus[order.orderStatusEmr as OrderStatusEmrEnum];
        }
        return 'N/A';
    }, [order.orderStatusEmr]);

    const errorStatuses = [
        OrderStatusEmrEnum.Error,
        OrderStatusEmrEnum.UiError,
        OrderStatusEmrEnum.MedOrderNotFoundError,
        OrderStatusEmrEnum.TeamNotFoundError,
        OrderStatusEmrEnum.MedOrderMissesDataError,
        OrderStatusEmrEnum.PatientNotFoundError,
        OrderStatusEmrEnum.SaveError,
        OrderStatusEmrEnum.DuplicateError,
        OrderStatusEmrEnum.PotentialDuplicateError,
    ];

    const inProcessStatuses = [
        OrderStatusEmrEnum.NotStarted,
        OrderStatusEmrEnum.Processing,
        OrderStatusEmrEnum.PendingImport,
    ];

    const emrSyncStatusIcon = useMemo(() => {
        if (
            typeof order.orderStatusEmr === 'number' &&
            order.orderStatusEmr >= 0
        ) {
            if (order.orderStatusEmr === OrderStatusEmrEnum.Success) {
                return '/assets/rx-images/patient-dashboard/emr_check.png';
            }

            if (errorStatuses.includes(order.orderStatusEmr)) {
                return '/assets/rx-images/patient-dashboard/emr_warn.png';
            }

            if (inProcessStatuses.includes(order.orderStatusEmr)) {
                return '/assets/rx-images/patient-dashboard/emr_inprocess.png';
            }
        }
        return undefined;
    }, [order.orderStatusEmr]);

    const medOrderStatus = useMemo(() => {
        if (
            typeof order.medicationStatus === 'number' &&
            order.medicationStatus >= 0
        ) {
            return {
                color: MedicationStatusLabels[
                    order.medicationStatus as MedicationStatusEnum
                ].color,
                label: MedicationStatusLabels[
                    order.medicationStatus as MedicationStatusEnum
                ].label,
            };
        }
        return { label: 'N/A', color: '' };
    }, [order.medicationStatus]);

    useEffect(() => {
        const handleScroll = () => {
            setVisible(false);
        };
        document.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    const orderStartOn = useMemo(() => {
        if (order.startDate) {
            return moment(order.startDate).format('M/D/YY');
        }
        return 'N/A';
    }, [order.startDate]);

    const orderEndOn = useMemo(() => {
        if (order.endDate) {
            return moment(order.endDate).format('M/D/YY');
        }
        return 'N/A';
    }, [order.endDate]);

    return (
        <OrderContainer title={<OrderTitle>{order.nameDisplay}</OrderTitle>}>
            <div className="d-flex py-1 lh-sm justify-content-between fw-light">
                <OrderItemInline
                    style={{ color: medOrderStatus.color }}
                    label="Status:"
                    value={medOrderStatus.label}
                />

                <div className="d-flex flex-row">
                    <div className="hcrx-order-gray fw-normal me-1">
                        EMR Sync Status:{' '}
                    </div>

                    {order.orderStatusEmr === OrderStatusEmrEnum.NA ? (
                        'N/A'
                    ) : (
                        <Tooltip
                            open={visible}
                            onOpenChange={(isOpen) => setVisible(isOpen)}
                            getPopupContainer={(triggerNode) =>
                                triggerNode.parentNode as HTMLElement
                            }
                            trigger={'click'}
                            destroyTooltipOnHide={true}
                            title={
                                <span className="text-light-gray">
                                    {emrSyncStatus}
                                </span>
                            }
                            overlayStyle={{ paddingTop: 5 }}
                            color={'#FFFFFF'}
                            showArrow={false}
                            placement="bottomRight"
                        >
                            <img
                                alt="emr"
                                src={emrSyncStatusIcon}
                                className="card-emr-icon"
                            />
                        </Tooltip>
                    )}
                </div>
            </div>
            <OrderItemInline
                style={{ color: source.color }}
                label="Source:"
                value={source.label}
            />
            <div
                className="d-flex py-1 lh-sm justify-content-between mt-2 gap-2"
                style={{ fontWeight: 300 }}
            >
                <OrderItem className="w-50" label="Unit Dosage" value={order.unitDosageDisplay}/>
                <OrderItem className="w-25" label="Duration" value={order.durationDisplay} />
                <OrderItem className="w-25" label="Route" value={order.routeDisplay} />
            </div>

            <OrderItem label="Frequency" value={order.frequencyDisplay} />
            <OrderItem className='mb-5' label="Quantity" value={order.quantity} />

            <div
                className="position-absolute bottom-0 start-0 w-100 d-flex align-items-center justify-content-between py-1 ps-2 pb-2"
                style={{ fontWeight: 300 }}
            >
                <div className="d-flex flex-row align-items-center card-calender-container ms-2">
                    <CalendarOutlined
                        rev
                        className="card-calender-icon me-1"
                        size={20}
                    />
                    <div className="hcrx-order-gray fw-normal">
                        Start on {orderStartOn}
                    </div>
                </div>
                <div className="d-flex flex-row align-items-center me-3">
                    <CalendarOutlined rev className="card-calender-icon me-1" />
                    <div className="hcrx-order-gray fw-normal">
                        End on {orderEndOn}
                    </div>
                </div>
            </div>
        </OrderContainer>
    );
};

const MedOrdersCard = ({
    data,
    isLoading,
    organizationImplementationDate,
}: {
    isLoading: boolean;
    data: MedOrderResponse[];
    organizationImplementationDate: string;
}) => {
    return (
        <OrderTab isLoading={isLoading}>
            <div className="fw-bold">
                Note:{' '}
                <span className="fw-light">
                    {`This may not be a complete medication history and reflects information available as of ${organizationImplementationDate}.`}
                </span>
            </div>
            <OrderTabGrid>
                {data &&
                    data.length !== 0 &&
                    data.map((order: any, index: number) => (
                        <MedOrderItem key={index} order={order} />
                    ))}
            </OrderTabGrid>
            {!isLoading && data && data.length === 0 && (
                <OrderNotFound>
                    There are no orders to be displayed.
                </OrderNotFound>
            )}
        </OrderTab>
    );
};

export default MedOrdersCard;
